/**
 * gRPC runtime port resolution — a DEPENDENCY-FREE leaf module.
 *
 * Extracted from transport.ts to break the import cycle
 *   transport.ts -> webLogger.ts -> transport.ts            and
 *   transport.ts -> useAuthStore.ts -> transport.ts
 * webLogger / useAuthStore / revitClient only ever needed the port helpers
 * (getGrpcPort / isGrpcRuntimeAvailable / getGrpcBaseUrl), not the transport
 * builder. By living here — with NO imports of webLogger, useAuthStore,
 * revitClient, or transport — those consumers can read the port without
 * pulling the whole transport (and its interceptors) into a cycle.
 *
 * Behaviour is identical to the original transport.ts logic; locked by
 * src/__tests__/grpcPortResolution.characterization.test.ts.
 */

const STORAGE_KEY = 'grpcPort';
const ENV_GRPC_PORT = process.env.REACT_APP_PIPEEX_GRPC_PORT;
const ENV_GRPC_HOST = process.env.REACT_APP_PIPEEX_GRPC_HOST || '127.0.0.1';

function isWebView2Environment(): boolean {
  try {
    return typeof window !== 'undefined' && !!(window as any).chrome?.webview;
  } catch {
    return false;
  }
}

function resolveInitialGrpcPort(): number | null {
  // 1. Explicit query parameter — always trust, save for session
  const params = new URLSearchParams(window.location.search);
  const rawFromQuery = params.get('grpcPort');
  if (rawFromQuery) {
    const parsed = parseInt(rawFromQuery, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      sessionStorage.setItem(STORAGE_KEY, String(parsed));
      localStorage.setItem(STORAGE_KEY, String(parsed));
      return parsed;
    }
  }

  // 2. Storage — only trust if running inside WebView2 (Revit) or same session
  //    In a plain browser tab without ?grpcPort, stored values are stale
  //    from a previous Revit session and should be ignored.
  if (isWebView2Environment()) {
    const rawFromStorage = sessionStorage.getItem(STORAGE_KEY);
    if (rawFromStorage) {
      const parsed = parseInt(rawFromStorage, 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const rawFromLocalStorage = localStorage.getItem(STORAGE_KEY);
    if (rawFromLocalStorage) {
      const parsed = parseInt(rawFromLocalStorage, 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        sessionStorage.setItem(STORAGE_KEY, String(parsed));
        return parsed;
      }
    }
  } else {
    // In a regular browser, only trust sessionStorage (same tab session)
    const rawFromSession = sessionStorage.getItem(STORAGE_KEY);
    if (rawFromSession) {
      const parsed = parseInt(rawFromSession, 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }

  // 3. Environment variable — only use when inside WebView2,
  //    otherwise a stale .env value makes the app think gRPC is active
  //    in a plain browser tab where no Revit server is running.
  if (ENV_GRPC_PORT && isWebView2Environment()) {
    const parsedFromEnv = parseInt(ENV_GRPC_PORT, 10);
    if (Number.isFinite(parsedFromEnv) && parsedFromEnv > 0) {
      return parsedFromEnv;
    }
  }

  return null;
}

let grpcPort: number | null = resolveInitialGrpcPort();

const targetChangeListeners = new Set<() => void>();

export function subscribeGrpcTargetChange(listener: () => void): () => void {
  targetChangeListeners.add(listener);
  return () => { targetChangeListeners.delete(listener); };
}

export function getGrpcPort(): number | null {
  return grpcPort;
}

/**
 * Persist a new port (or clear it). Pure state + storage side-effects — it does
 * NOT touch the transport cache. transport.setGrpcPort() wraps this and also
 * invalidates the cached Transport so the next getGrpcTransport() rebuilds.
 */
export function setGrpcPortValue(port: number | null): void {
  const previousTarget = getGrpcBaseUrl();
  grpcPort = port;
  if (getGrpcBaseUrl() !== previousTarget) {
    targetChangeListeners.forEach((listener) => listener());
  }

  if (port && Number.isFinite(port) && port > 0) {
    const value = String(port);
    sessionStorage.setItem(STORAGE_KEY, value);
    localStorage.setItem(STORAGE_KEY, value);
    return;
  }

  sessionStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_KEY);
}

export function isGrpcRuntimeAvailable(): boolean {
  return getGrpcPort() !== null;
}

/** True when the page runs inside the plugin's WebView2 in Revit. */
export function isWebView2Runtime(): boolean {
  return isWebView2Environment();
}

export function getGrpcBaseUrl(): string | null {
  const port = getGrpcPort();
  if (!port) {
    return null;
  }

  return `http://${ENV_GRPC_HOST}:${port}`;
}
